import { useEffect, useState } from "react";
import { api, setToken, getToken } from "rsa/api";
import MapView from "rsa/MapView";
import RequestTracker from "./RequestTracker";
import "rsa/rsa.scss";

const DEFAULT_LOC = { lat: 19.076, lng: 72.8777 }; // Mumbai fallback if geolocation denied

export default function GuestEmergency() {
  const [step, setStep] = useState(0);          // 0 sos, 1 phone, 2 otp, 3 vehicle, 4 details, 5 tracking
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [phone, setPhone] = useState("");
  const [devCode, setDevCode] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [reg, setReg] = useState("");
  const [knownVehicle, setKnownVehicle] = useState(null);

  const [types, setTypes] = useState([]);
  const [serviceTypeId, setServiceTypeId] = useState(null);
  const [loc, setLoc] = useState(null);
  const [severity, setSeverity] = useState("high");
  const [ambulance, setAmbulance] = useState(false);
  const [fire, setFire] = useState(false);
  const [police, setPolice] = useState(false);
  const [requestId, setRequestId] = useState(null);

  const token = getToken("guest");
  const run = async (fn) => { setBusy(true); setErr(""); try { await fn(); } catch (e) { setErr(e.message); } finally { setBusy(false); } };

  useEffect(() => { api("GET", "/rsa/service-types").then(setTypes).catch(() => {}); }, []);

  const getLocation = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(DEFAULT_LOC);
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve(DEFAULT_LOC),
        { enableHighAccuracy: true, timeout: 8000 }
      );
    });

  const sendOtp = () => run(async () => {
    const r = await api("POST", "/auth/guest/otp", { body: { phone } });
    if (r.devCode) setDevCode(r.devCode);
    setStep(2);
  });
  const verifyOtp = () => run(async () => {
    const r = await api("POST", "/auth/guest/otp/verify", { body: { phone, code } });
    setToken("guest", r.token);
    setStep(3);
  });
  const lookupVehicle = () => run(async () => {
    if (!reg) return;
    try { setKnownVehicle(await api("GET", `/rsa/vehicles/${encodeURIComponent(reg)}`, { scope: "guest" })); }
    catch { setKnownVehicle(null); }
  });
  const goDetails = () => run(async () => { setLoc(await getLocation()); setStep(4); });

  const submit = () => run(async () => {
    const st = types.find((t) => t.id === serviceTypeId);
    const r = await api("POST", "/rsa/requests", {
      scope: "guest",
      body: {
        serviceTypeId, guestName: name, vehicleRegistration: reg,
        lat: loc.lat, lng: loc.lng, severity,
        ambulanceRequested: ambulance || (st?.is_ambulance_eligible && severity === "critical"),
        fireRequested: fire, policeRequested: police,
        description: `Guest emergency — ${st?.name}`,
      },
    });
    setRequestId(r.request.id);
    setStep(5);
  });

  return (
    <div className="avah-rsa">
      <div className="avah-rsa__top">
        <h1>🚨 Avahan Emergency</h1>
        <p>Roadside &amp; collision assistance — no account needed</p>
      </div>
      <div className="avah-rsa__shell">
        {step === 0 && (
          <div className="avah-rsa__card" style={{ textAlign: "center" }}>
            <p style={{ marginTop: 0, color: "#6b7280" }}>
              Had an accident or breakdown? Get help from the nearest collision-care partner in minutes.
            </p>
            <button className="avah-rsa__btn avah-rsa__btn--sos" onClick={() => setStep(1)}>
              Get Emergency Help
            </button>
            <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 0 }}>
              We'll verify your number, then alert nearby vendors instantly.
            </p>
          </div>
        )}

        {step === 1 && (
          <div className="avah-rsa__card">
            <label className="avah-rsa__label">Your mobile number</label>
            <input className="avah-rsa__input" inputMode="numeric" placeholder="10-digit mobile"
              value={phone} onChange={(e) => setPhone(e.target.value)} />
            <p style={{ fontSize: 12, color: "#6b7280" }}>We send a one-time code to confirm it's you.</p>
            <button className="avah-rsa__btn avah-rsa__btn--primary" disabled={busy || phone.length < 8}
              onClick={sendOtp}>{busy ? "Sending…" : "Send OTP"}</button>
            {err && <div className="avah-rsa__err">{err}</div>}
          </div>
        )}

        {step === 2 && (
          <div className="avah-rsa__card">
            <label className="avah-rsa__label">Enter the OTP sent to {phone}</label>
            <input className="avah-rsa__input" inputMode="numeric" placeholder="6-digit code"
              value={code} onChange={(e) => setCode(e.target.value)} />
            {devCode && <div className="avah-rsa__dev">Demo mode — your OTP is <b>{devCode}</b></div>}
            <button className="avah-rsa__btn avah-rsa__btn--primary" style={{ marginTop: 12 }}
              disabled={busy || code.length < 4} onClick={verifyOtp}>
              {busy ? "Verifying…" : "Verify"}</button>
            {err && <div className="avah-rsa__err">{err}</div>}
          </div>
        )}

        {step === 3 && (
          <div className="avah-rsa__card">
            <label className="avah-rsa__label">Your name</label>
            <input className="avah-rsa__input" placeholder="Full name" value={name}
              onChange={(e) => setName(e.target.value)} />
            <label className="avah-rsa__label" style={{ marginTop: 14 }}>Vehicle registration number</label>
            <input className="avah-rsa__input" placeholder="e.g. MH12AB1234" value={reg}
              onChange={(e) => setReg(e.target.value.toUpperCase())} onBlur={lookupVehicle} />
            {knownVehicle && (
              <div className="avah-rsa__vehicle">
                ✅ Registered vehicle found: <b>{knownVehicle.brand} {knownVehicle.model}</b> ({knownVehicle.fuel})
                {knownVehicle.has_insurance && <> · insurance on file 🛡️</>}
                <br />Owner: {knownVehicle.owner_name} ({knownVehicle.owner_phone})
              </div>
            )}
            <button className="avah-rsa__btn avah-rsa__btn--primary" style={{ marginTop: 14 }}
              disabled={busy || !name || !reg} onClick={goDetails}>
              {busy ? "Locating…" : "Continue"}</button>
            {err && <div className="avah-rsa__err">{err}</div>}
          </div>
        )}

        {step === 4 && (
          <>
            <div className="avah-rsa__card">
              <label className="avah-rsa__label">What happened?</label>
              <div className="avah-rsa__reasons">
                {types.map((t) => (
                  <button key={t.id} type="button"
                    className={`avah-rsa__reason${serviceTypeId === t.id ? " avah-rsa__reason--on" : ""}`}
                    onClick={() => { setServiceTypeId(t.id); if (t.is_ambulance_eligible) setSeverity("critical"); }}>
                    <b>{t.name}</b><span>from ₹{Math.round(t.base_fee)}</span>
                  </button>
                ))}
              </div>
              <label className="avah-rsa__label" style={{ marginTop: 16 }}>Severity</label>
              <div className="avah-rsa__row">
                {["low", "medium", "high", "critical"].map((s) => (
                  <button key={s} type="button"
                    className={`avah-rsa__pill${severity === s ? " avah-rsa__pill--live" : ""}`}
                    onClick={() => setSeverity(s)} style={{ cursor: "pointer", border: "none" }}>{s}</button>
                ))}
              </div>
              <label style={{ display: "flex", gap: 8, marginTop: 16, alignItems: "center", fontSize: 14 }}>
                <input type="checkbox" checked={ambulance} onChange={(e) => setAmbulance(e.target.checked)} />
                🚑 Also send an ambulance (severe injury)
              </label>
              <label style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", fontSize: 14 }}>
                <input type="checkbox" checked={fire} onChange={(e) => setFire(e.target.checked)} />
                🚒 Alert fire brigade (fire / smoke / fuel leak)
              </label>
              <label style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", fontSize: 14 }}>
                <input type="checkbox" checked={police} onChange={(e) => setPolice(e.target.checked)} />
                👮 Alert police (accident / dispute / safety)
              </label>
            </div>
            <div className="avah-rsa__card" style={{ padding: 12 }}>
              <div className="avah-rsa__label" style={{ padding: "0 4px 8px" }}>📍 Your location (auto-detected)</div>
              <MapView pickup={loc} center={loc} height={220} />
            </div>
            <button className="avah-rsa__btn avah-rsa__btn--primary" style={{ marginTop: 14 }}
              disabled={busy || !serviceTypeId} onClick={submit}>
              {busy ? "Sending SOS…" : "🚨 Send Emergency Request"}</button>
            {err && <div className="avah-rsa__err">{err}</div>}
          </>
        )}

        {step === 5 && requestId && (
          <RequestTracker requestId={requestId} token={token} pickup={loc} />
        )}
      </div>
    </div>
  );
}
